// src/services/gymTheme.service.js

import { supabase } from "../supabase/client";

const CACHE_KEY    = "lesvita_gym_theme";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_THEME = {
  gym_id:                null,
  gym_name:              "LesVita",
  primary_color:         "#1428A0",
  secondary_color:       "#FBBC04",
  background_color:      "#00095C",
  splash_bg_color:       "#00095C",
  splash_bg_image_url:   null,
  logo_url:              null,
  navbar_bg_color:       "#1428A0",
  navbar_active_color:   "#fff",
  tab_active_bg:         "#FBBC04",
  tab_active_text:       "#1428A0",
  button_primary_bg:     "#FBBC04",
  button_primary_text:   "#1428A0",
  label_highlight_color: "#FBBC04",
  card_accent_bg:        "#FBBC04",
  // ── Novas cores estruturadas (light) ──────────────────────────────────
  navbar_inactive_color: null,      // null → app cai no fallback (accent)
  app_bg_color:          null,      // null → mantém o --lesvita-bg atual
  brand_main_color:      "#201e1e", // nova "cor principal" da marca
  // ── Dark mode (null = app usa fallback padrão LesVita dark) ────────────
  dark_primary_color:       null,
  dark_accent_color:        null,
  dark_navbar_bg_color:     null,
  dark_navbar_active_color: null,
  dark_splash_bg_color:     null,
  dark_surface_color:       null,
  dark_navbar_inactive_color: null, // null → fallback (accent)
  dark_app_bg_color:          null, // null → mantém o --lesvita-bg atual
  dark_brand_main_color:      null, // null → fallback (#201e1e)
  // ───────────────────────────────────────────────────────────────────────
  theme_version:         null,
  is_default:            true,
};

const isValidHex = (color) =>
  typeof color === "string" && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color);

const c         = (value, fallback) => (isValidHex(value) ? value : fallback);
const cNullable = (value)           => (isValidHex(value) ? value : null);

const normalizeTheme = (raw, gymReferral) => {
  if (!raw) return null;

  const p  = c(raw.brand_primary_color,  DEFAULT_THEME.primary_color);
  const a  = c(raw.brand_accent_color,   DEFAULT_THEME.secondary_color);
  const bg = c(raw.splash_bg_color,      DEFAULT_THEME.background_color);

  return {
    gym_id:                gymReferral?.CODE || raw.referral_code || null,
    gym_name:              gymReferral?.GYM_NAME || raw.gym_name || "Academia",
    primary_color:         p,
    secondary_color:       c(raw.brand_accent_color, DEFAULT_THEME.secondary_color),
    background_color:      bg,
    splash_bg_color:       bg,
    splash_bg_image_url:   raw.splash_bg_image_url || null,
    logo_url:              gymReferral?.LOGO_URL || raw.logo_url || null,
    navbar_bg_color:       c(raw.navbar_bg_color,        p),
    navbar_active_color:   c(raw.navbar_active_color,    a),
    tab_active_bg:         c(raw.tab_active_bg,          a),
    tab_active_text:       c(raw.tab_active_text,        p),
    button_primary_bg:     c(raw.button_primary_bg,      a),
    button_primary_text:   c(raw.button_primary_text,    p),
    label_highlight_color: c(raw.label_highlight_color,  a),
    card_accent_bg:        c(raw.card_accent_bg,         a),
    // ── Novas cores estruturadas (light) — null se não configurado ──────
    navbar_inactive_color: cNullable(raw.navbar_inactive_color),
    app_bg_color:          cNullable(raw.app_bg_color),
    brand_main_color:      c(raw.brand_main_color, DEFAULT_THEME.brand_main_color),
    // ── Dark mode — null se não configurado pelo admin ──────────────────
    dark_primary_color:       cNullable(raw.dark_primary_color),
    dark_accent_color:        cNullable(raw.dark_accent_color),
    dark_navbar_bg_color:     cNullable(raw.dark_navbar_bg_color),
    dark_navbar_active_color: cNullable(raw.dark_navbar_active_color),
    dark_splash_bg_color:     cNullable(raw.dark_splash_bg_color),
    dark_surface_color:       cNullable(raw.dark_surface_color),
    dark_navbar_inactive_color: cNullable(raw.dark_navbar_inactive_color),
    dark_app_bg_color:          cNullable(raw.dark_app_bg_color),
    dark_brand_main_color:      cNullable(raw.dark_brand_main_color),
    // ───────────────────────────────────────────────────────────────────
    // Guarda a versão para detectar mudanças no painel da academia
    theme_version:         raw.theme_version || raw.updated_at || null,
    is_default:            false,
  };
};

const readCache = (gymCode) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached    = JSON.parse(raw);
    const isExpired = Date.now() - (cached.saved_at || 0) > CACHE_TTL_MS;
    const isSameGym = cached.theme?.gym_id === gymCode;
    if (!isExpired && isSameGym) return cached.theme;
    return null;
  } catch {
    return null;
  }
};

const writeCache = (theme) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ theme, saved_at: Date.now() }));
  } catch {}
};

export const clearThemeCache = () => {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
};

// Busca apenas o theme_version atual do banco — query leve, sem buscar tudo
export const fetchThemeVersion = async (gymCode) => {
  try {
    const { data, error } = await supabase
      .from("gym_partner_profiles")
      .select("theme_version, updated_at")
      .eq("referral_code", gymCode)
      .single();

    if (error || !data) return null;
    return data.theme_version || data.updated_at || null;
  } catch {
    return null;
  }
};

export const fetchGymTheme = async (gymReferral) => {
  if (!gymReferral?.APPLIED || !gymReferral?.CODE) return DEFAULT_THEME;

  const gymCode = gymReferral.CODE;
  const cached  = readCache(gymCode);

  if (cached) {
    const remoteVersion = await fetchThemeVersion(gymCode);
    const cacheVersion  = cached.theme_version;
    if (remoteVersion && cacheVersion && remoteVersion === cacheVersion) {
      return cached;
    }
    clearThemeCache();
  }

  try {
    const { data, error } = await supabase
      .from("gym_partner_profiles")
      .select(`
        id,
        gym_name,
        referral_code,
        logo_url,
        brand_primary_color,
        brand_secondary_color,
        brand_accent_color,
        splash_bg_color,
        splash_bg_image_url,
        navbar_bg_color,
        navbar_active_color,
        tab_active_bg,
        tab_active_text,
        button_primary_bg,
        button_primary_text,
        label_highlight_color,
        card_accent_bg,
        loading_bg_color,
        navbar_inactive_color,
        app_bg_color,
        brand_main_color,
        dark_primary_color,
        dark_accent_color,
        dark_navbar_bg_color,
        dark_navbar_active_color,
        dark_splash_bg_color,
        dark_surface_color,
        dark_navbar_inactive_color,
        dark_app_bg_color,
        dark_brand_main_color,
        theme_version,
        updated_at
      `)
      .eq("referral_code", gymCode)
      .single();

    if (error || !data) {
      const partialTheme = {
        ...DEFAULT_THEME,
        gym_id:     gymCode,
        gym_name:   gymReferral.GYM_NAME || "Academia",
        logo_url:   gymReferral.LOGO_URL || null,
        is_default: true,
      };
      writeCache(partialTheme);
      return partialTheme;
    }

    const theme = normalizeTheme(data, gymReferral);
    writeCache(theme);
    return theme;
  } catch (err) {
    console.warn("[gymTheme.service] Falha ao buscar tema:", err);
    return {
      ...DEFAULT_THEME,
      gym_id:     gymCode,
      gym_name:   gymReferral.GYM_NAME || "Academia",
      logo_url:   gymReferral.LOGO_URL || null,
      is_default: true,
    };
  }
};